/**
 * `entities/taxonomy-campaign` mutations.
 *
 * Same conventions as the sibling taxonomy slices: direct hey-api calls, every
 * mutation invalidates the campaign list, and toasts live here so every write
 * gives consistent feedback.
 *
 * Every request that carries its own `onError` toast also sets
 * `SUPPRESS_ERROR_NOTIFICATION_HEADER`. Without it the shared problem-details
 * interceptor toasts the same failure again, and the user gets two stacked
 * errors for one action. `useCreateTaxonomyCampaign` is the exception: it has
 * no `onError` at all, so the interceptor's toast is its only feedback.
 *
 * These invalidate CAMPAIGNS only, though a campaign write also changes what the
 * generation list's dataset query returns — a delete detaches taxonomies, so
 * their `campaignId` is stale afterwards. Reaching into another entity's key
 * factory from here is what FSD's cross-entity rule forbids, and rightly: the
 * page composing both is the thing that knows they are rendered as one tree, so
 * it passes an `onSuccess` that refreshes the other half.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { i18n } from '@/shared/i18n';
import { assetsHttp } from '@/lib/api/http-client';
import { SUPPRESS_ERROR_NOTIFICATION_HEADER } from '@/lib/api/interceptors/problem-details.interceptor';
import { parseWriteResponse, UnreadableWriteError } from '@/lib/api/parse-response';
import { track } from '@/shared/analytics';
import type { TaxonomyCampaign } from '../model/types';
import { TAXONOMY_CAMPAIGN_QUERY_KEYS } from './query-keys';
import { CAMPAIGNS_URL } from './queries';
import { taxonomyCampaignSchema } from './schemas';

export interface CreateTaxonomyCampaignPayload {
  name: string;
  description?: string;
}

export interface UpdateTaxonomyCampaignPayload {
  id: string;
  name?: string;
  description?: string | null;
}

function useInvalidateCampaigns() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: TAXONOMY_CAMPAIGN_QUERY_KEYS.list() });
  };
}

export function useCreateTaxonomyCampaign(options?: {
  onSuccess?: (created: TaxonomyCampaign) => void;
}) {
  const invalidate = useInvalidateCampaigns();

  return useMutation({
    mutationFn: async (payload: CreateTaxonomyCampaignPayload) => {
      const response = await assetsHttp.post({
        throwOnError: true,
        url: CAMPAIGNS_URL,
        body: payload,
      });
      return parseWriteResponse(taxonomyCampaignSchema, response, invalidate);
    },
    onSuccess: (created) => {
      invalidate();
      track('taxonomy_campaign_created', { campaign_id: created.id });
      options?.onSuccess?.(created);
    },
    /*
     * No success toast. The only caller creates a campaign as the first half of creating
     * a taxonomy, so a success toast here would be followed immediately by the
     * taxonomy's own — two notifications for one action the user took once.
     * A refused create surfaces through the shared problem-details interceptor, which
     * only sees error responses, so a created campaign with an unreadable reply is
     * reported here.
     */
    onError: (error) => {
      if (error instanceof UnreadableWriteError) {
        enqueueSnackbar(i18n.t('knowledge:taxonomy.campaign.createdUnreadable'), {
          variant: 'warning',
        });
      }
    },
  });
}

export function useUpdateTaxonomyCampaign(options?: { onSuccess?: () => void }) {
  const invalidate = useInvalidateCampaigns();

  return useMutation({
    mutationFn: async ({ id, ...body }: UpdateTaxonomyCampaignPayload) => {
      const response = await assetsHttp.patch({
        throwOnError: true,
        url: `${CAMPAIGNS_URL}/{id}`,
        path: { id },
        // eslint-disable-next-line i18next/no-literal-string -- a wire header value, not copy
        headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
        body,
      });
      return parseWriteResponse(taxonomyCampaignSchema, response, invalidate);
    },
    onSuccess: (updated) => {
      invalidate();
      track('taxonomy_campaign_updated', { campaign_id: updated.id });
      enqueueSnackbar(i18n.t('knowledge:taxonomy.campaign.updated'), { variant: 'success' });
      options?.onSuccess?.();
    },
    onError: () => {
      enqueueSnackbar(i18n.t('knowledge:taxonomy.campaign.failedUpdate'), { variant: 'error' });
    },
  });
}

export function useDeleteTaxonomyCampaign(options?: { onSuccess?: () => void }) {
  const invalidate = useInvalidateCampaigns();

  return useMutation({
    mutationFn: async (id: string) => {
      await assetsHttp.delete({
        throwOnError: true,
        url: `${CAMPAIGNS_URL}/{id}`,
        path: { id },
        // eslint-disable-next-line i18next/no-literal-string -- a wire header value, not copy
        headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
      });
      return id;
    },
    onSuccess: (id) => {
      invalidate();
      track('taxonomy_campaign_deleted', { campaign_id: id });
      enqueueSnackbar(i18n.t('knowledge:taxonomy.campaign.deleted'), { variant: 'success' });
      options?.onSuccess?.();
    },
    onError: () => {
      enqueueSnackbar(i18n.t('knowledge:taxonomy.campaign.failedDelete'), { variant: 'error' });
    },
  });
}
